import React, { FC, useMemo, useRef, useEffect } from "react";
import useStorefrontApi from "../../hooks/useStorefrontApi";
import styled from "styled-components";
import { SwiperCarousel, SwiperSlide } from "../Swiper";
import { Mousewheel, Keyboard, Navigation as SwiperNavigation } from "swiper/modules";
import { buttonReset, colors, font } from "../../lib/sharedStyles";

export interface FormattedVariant {
  id: string;
  sku: string | null;
  title: string;
  imageUrl: string;
  url: string;
  tags?: string[];
  product_type?: string;
  price: {
    amount: string;
    currencyCode: string;
  };
}

const CarouselWrapper = styled.div`
  flex-direction: column;

  @media (min-width: 1024px) {
    margin-bottom: 120px;
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  margin-top: 15px;

  swiper-slide {
    height: auto;

    > div {
      height: 100%;
    }
  }
`;

const CarouselHeader = styled.div<{
  $hasHeading: boolean;
  $forceMinHeight: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ $hasHeading }) =>
    $hasHeading ? "space-between" : "flex-end"};

  h2 {
    font-size: 24px;
    text-transform: lowercase;
  }
  h2::first-letter {
    text-transform: uppercase;
  }

  @media (min-width: 768px) {
    h2 {
      font-size: 32px;
    }
  }

  @media (min-width: 1024px) {
    margin-top: ${({ $hasHeading }) => ($hasHeading ? "0" : "-64px")};
    min-height: ${({ $forceMinHeight }) =>
      $forceMinHeight ? "32px" : "0"};
  }
`;

const Heading = styled.h2`
  ${font("Futura", 600, "24px", "36px")};
  letter-spacing: -0.48px;
  color: ${colors.fender.primaryDark};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 36px;
    line-height: 34px;
    letter-spacing: -0.72px;
  }
`;

const Navigation = styled.div<{ $hasHeading: boolean }>`
  gap: 8px;
  height: ${({ $hasHeading }) => ($hasHeading ? "3.44rem" : "auto")};
  margin-right: 72px;

  @media only screen and (max-width: 600px) {
    margin-right: 16px;
  }

  @media (min-width: 1024px) {
    display: flex;
  }

  button {
    ${buttonReset()};
    width: 32px;
    height: 32px;
    transition: background-color 0.2s ease, border-radius 0.2s ease;
    cursor: pointer;

    &:hover {
      background-color: #f5f5f5;
      border-radius: 100%;
    }

    svg {
      vertical-align: middle;
    }

    // Rotate "next" arrow
    &:not(:first-of-type) {
      svg {
        transform: rotate(180deg);
      }
    }

    // This class is dynamically added by Swiper.
    &.swiper-button-disabled {
      svg path {
        fill: #e5e5e5;
      }
    }
  }
`;

interface BundleWrapperProps {
  variantIds: Array<string | number>;
  Component: FC<{ variant: FormattedVariant }>;
  className?: string;
  children?: React.ReactNode;
  heading?: string;
}

const toGid = (id: string | number) =>
  String(id).startsWith("gid://")
    ? String(id)
    : `gid://shopify/ProductVariant/${id}`;

const buildQuery = (gids: string[]) => `
  query VariantNodes {
    nodes(ids: [${gids.map(g => `"${g}"`).join(",")}]) {
      ... on ProductVariant {
        id
        sku
        image { url }
        price: priceV2 {
          amount
          currencyCode
        }
        product { handle title tags productType }
      }
    }
  }
`;

const BundleWrapper: FC<BundleWrapperProps> = ({
  variantIds,
  Component,
  className,
  heading,
}) => {
  const gids = variantIds.map(toGid);
  const { storefrontData }: { storefrontData: any } = useStorefrontApi(
    buildQuery(gids)
  );

  const variants = useMemo(() => {
    const nodes = storefrontData?.nodes ?? [];
    return nodes
      .filter(Boolean)
      .map((node: any) => ({
        id: node.id,
        sku: node.sku ?? null,
        title: node.product?.title ?? "",
        imageUrl: node.image?.url ?? "",
        tags: node.product?.tags ?? [],
        product_type: node.product?.productType ?? "",
        url: node.product
          ? `/products/${node.product.handle}?variant=${encodeURIComponent(
              node.id
            )}`
          : "",
        price: node.price,
      }));
  }, [storefrontData]);

  const uniqueCarouselId = useRef(
    `bundle-${Math.random().toString(36).substring(2, 9)}`
  );

  if (!variants.length) return null;

  const breakpoints = {
    // mobile first – show 1.2 slides (so ~20% peek of slide #2)
    0: { slidesPerView: 1.2, spaceBetween: 16 },
    768: { slidesPerView: 2, spaceBetween: 16 },
    1024: { slidesPerView: 4, spaceBetween: 16 },
  };

  const navigation = {
    prevEl: `.swiper-button-prev-${uniqueCarouselId.current}`,
    nextEl: `.swiper-button-next-${uniqueCarouselId.current}`,
  };

  return (
    <CarouselWrapper className={className}>
      <CarouselHeader $hasHeading={true} $forceMinHeight={false}>
        {heading && <Heading>{heading}</Heading>}
        <Navigation $hasHeading={true}>
          {Object.keys(navigation).map((key) => (
            // @ts-ignore untyped object, substring to remove leading "."
            <button key={key} className={navigation[key].substring(1)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
              >
                <path
                  fill="#191A1F"
                  d="M11 6.988a.75.75 0 0 0 0-1.5v1.5ZM.47 5.708a.75.75 0 0 0 0 1.06l4.773 4.774a.75.75 0 1 0 1.06-1.061L2.061 6.238l4.242-4.242A.75.75 0 0 0 5.243.935L.47 5.708ZM11 5.488H1v1.5h10v-1.5Z"
                />
              </svg>
            </button>
          ))}
        </Navigation>
      </CarouselHeader>

      <CarouselContainer>
        <SwiperCarousel
          modules={[Mousewheel, Keyboard, SwiperNavigation]}
          breakpoints={breakpoints}
          navigation={navigation}
          loop
          autoHeight
        >
          {variants.map((v: any) => (
            <SwiperSlide key={v.id}>
              <Component variant={v} />
            </SwiperSlide>
          ))}
        </SwiperCarousel>
      </CarouselContainer>
    </CarouselWrapper>
  );
};

export default BundleWrapper;
